import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';

import { useDesignSystemIntl } from '../hooks/use-design-system-intl';

import type { FileUploadButtonProps } from './file-upload.types';
import { useFileUploadDropzone } from './use-file-upload-dropzone';

const FileUploadButton = React.forwardRef<HTMLElement, FileUploadButtonProps>(
  (
    {
      'data-testid': testId,
      asChild = false,
      children,
      onFilesAccepted,
      onFilesRejected,
      accept,
      multiple = false,
      maxFiles = 0,
      maxSize,
      minSize,
      disabled = false,
      inputLabel,
      ...props
    },
    ref
  ) => {
    const intl = useDesignSystemIntl();
    const resolvedInputLabel =
      inputLabel ??
      intl.formatMessage({
        defaultMessage: 'Upload file',
        id: '0zMS4MPykD',
        description: 'Default accessible name for the hidden file-picker input',
      });

    const { open, getInputProps } = useFileUploadDropzone({
      noClick: true,
      noDrag: true,
      noKeyboard: true,
      multiple,
      accept,
      maxFiles,
      maxSize,
      minSize,
      disabled,
      onFilesAccepted,
      onFilesRejected,
      method: 'browse',
    });

    const composedOnClick = (event: React.MouseEvent<HTMLElement>) => {
      props.onClick?.(event);
      if (!event.defaultPrevented) open();
    };

    const hiddenInput = (
      <input
        {...getInputProps({ 'aria-label': resolvedInputLabel })}
        data-testid={testId ? `${testId}-input` : undefined}
      />
    );

    if (asChild) {
      // Slot's typing extends HTMLAttributes<HTMLElement> and so doesn't list
      // `disabled`, but it forwards arbitrary props to the child element.
      // Forwarding `disabled` is intentional: it disables the consumer's
      // trigger automatically rather than requiring them to mirror it.
      const slotExtraProps = disabled ? { disabled: true } : {};
      return (
        <>
          {hiddenInput}
          <Slot
            ref={ref}
            data-testid={testId}
            {...props}
            {...slotExtraProps}
            onClick={composedOnClick}
          >
            {children}
          </Slot>
        </>
      );
    }

    return (
      <>
        {hiddenInput}
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          type="button"
          data-testid={testId}
          {...props}
          onClick={composedOnClick}
          disabled={disabled}
        >
          {children}
        </button>
      </>
    );
  }
);
FileUploadButton.displayName = 'FileUploadButton';

export { FileUploadButton };
